import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Icon } from '@factorialco/f0-react'
import { Person } from '@factorialco/f0-react/icons/app'
import { Moment } from 'moment'

import { observer } from 'mobx-react'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'

import { useCalculateSummaryUnassignedShifts } from 'modules/shift_management/hooks/useCalculateSummaryUnassignedShifts'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useUnassignedShifts } from 'modules/shift_management/hooks/useUnassignedShifts'
import type { Shift } from 'modules/shift_management/lib/types'
import type Coverages from 'modules/shift_management/store/internal/collections/Coverages'
import type CoveragesByDay from 'modules/shift_management/store/internal/collections/CoveragesByDay'
import UnassignedShifts from 'modules/shift_management/store/internal/collections/UnassignedShifts'
import type UnassignedShift from 'modules/shift_management/store/internal/models/UnassignedShift'

interface Props {
  date: Moment
  coverageShifts: Shift[]
  peopleData: Coverages | CoveragesByDay
}

const CoverageShiftsSummary = ({ date, coverageShifts, peopleData }: Props) => {
  const [unassignedShifts] = useUnassignedShifts()
  const [{ view }] = useSchedule()

  const shiftsByDateWeekly = useUnsafeMemo(() => {
    return coverageShifts.filter((shift) => shift.isDate(date))
  }, [coverageShifts, date])

  const unassignedShiftsByDate = useUnsafeMemo(
    () => new UnassignedShifts(unassignedShifts).filterByDate(date),
    [unassignedShifts, date]
  )

  const { totalCounters, totalTime } = useCalculateSummaryUnassignedShifts({
    unassignedShifts: unassignedShiftsByDate,
  })

  const assignedShiftsWeekly = useUnsafeMemo(
    () =>
      unassignedShiftsByDate.flatMap((unassignedShift: UnassignedShift) =>
        unassignedShift.assignedShifts(shiftsByDateWeekly)
      ),
    [unassignedShiftsByDate, shiftsByDateWeekly]
  )
  const totalAssignedShiftsWeekly = assignedShiftsWeekly.length

  const numberOfPeopleInTable = view.isDaily
    ? 'getNumberOfShifts' in peopleData
      ? peopleData.getNumberOfShifts(date)
      : 0
    : 'getNumberOfEmployees' in peopleData
      ? peopleData.getNumberOfEmployees(date)
      : 0

  return (
    <Box
      flexDirection="row"
      alignItems="center"
      width="full"
      height="full"
      justifyContent="center"
      gap="s2"
    >
      {view.isWeekly && <F0Icon icon={Person} size="sm" color="secondary" />}
      <Text size="100" weight="normal" className="text-f1-foreground">
        {view.isWeekly ? `${totalAssignedShiftsWeekly} / ${totalCounters}` : numberOfPeopleInTable}
      </Text>
    </Box>
  )
}

export default observer(CoverageShiftsSummary)
